import React, { useEffect, useState } from "react";
import { api, API_BASE } from "@/lib/api";
import { Panel } from "@/components/kcc/Panel";
import { KpiGrid, KpiCard, StatusChip } from "@/components/kcc/Kpi";
import { DataTable, statusToChip } from "@/components/kcc/DataTable";
import { Download, Loader2, Search, Sparkles, Send, ShieldCheck } from "lucide-react";

export function PredictivePanel() {
    const [data, setData] = useState(null);
    useEffect(() => { api.get("/refinery-maintenance/predictive").then(({ data }) => setData(data)); }, []);
    const cols = (extra) => [
        { key: "tag", label: "Tag" },
        { key: "name", label: "Asset" },
        { key: "area", label: "Area" },
        ...extra,
        { key: "rul_days", label: "RUL (d)", render: (r) => <span className={r.rul_days < 30 ? "text-kcc-red" : r.rul_days < 60 ? "text-kcc-amber" : "kcc-text-silver"}>{r.rul_days}</span> },
    ];
    return (
        <div className="space-y-6">
            <KpiGrid kpis={data?.kpis || []} accent="amber" cols={4} />
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                <Panel title="Vibration Analysis" subtitle="Assets above 6 mm/s ISO threshold" accent="red">
                    <DataTable columns={cols([{ key: "vibration_mms", label: "Vib mm/s", render: (r) => <span className="text-kcc-red">{r.vibration_mms}</span> }])} rows={data?.vibration_watch || []} testid="vibration-watch" />
                </Panel>
                <Panel title="Thermal Imaging" subtitle="Δ Temp anomalies" accent="amber">
                    <DataTable columns={cols([{ key: "thermal_dt_c", label: "ΔT °C" }])} rows={data?.thermal_watch || []} testid="thermal-watch" />
                </Panel>
                <Panel title="Oil Analysis" subtitle="Lowest oil condition score" accent="cobalt">
                    <DataTable columns={cols([{ key: "oil_condition", label: "Oil (/100)", render: (r) => <span className={r.oil_condition < 60 ? "text-kcc-red" : "text-kcc-amber"}>{r.oil_condition}</span> }])} rows={data?.oil_watch || []} testid="oil-watch" />
                </Panel>
                <Panel title="Ultrasonic Inspection" subtitle="High dB signatures" accent="purple">
                    <DataTable columns={cols([{ key: "ultrasonic_db", label: "dB" }])} rows={data?.ultrasonic_watch || []} testid="ultrasonic-watch" />
                </Panel>
                <Panel title="Bearing Monitoring" subtitle="Lowest bearing health" accent="red">
                    <DataTable columns={cols([{ key: "bearing_health", label: "Bearing (/100)", render: (r) => <span className={r.bearing_health < 60 ? "text-kcc-red" : "text-kcc-amber"}>{r.bearing_health}</span> }])} rows={data?.bearing_watch || []} testid="bearing-watch" />
                </Panel>
                <Panel title="Gearbox Monitoring" subtitle="Lowest gearbox health" accent="amber">
                    <DataTable columns={cols([{ key: "gearbox_health", label: "Gearbox (/100)" }])} rows={data?.gearbox_watch || []} testid="gearbox-watch" />
                </Panel>
                <Panel title="Motor Current Analysis" subtitle="Deviation from baseline" accent="cobalt">
                    <DataTable columns={cols([{ key: "mca_delta_pct", label: "MCA Δ %" }])} rows={data?.mca_watch || []} testid="mca-watch" />
                </Panel>
                <Panel title="Remaining Useful Life" subtitle="Closest to failure" accent="red">
                    <DataTable columns={cols([{ key: "health_index", label: "Health" }])} rows={data?.rul_watch || []} testid="rul-watch" />
                </Panel>
            </div>
        </div>
    );
}

export function ReliabilityPanel() {
    const [data, setData] = useState(null);
    useEffect(() => { api.get("/refinery-maintenance/reliability").then(({ data }) => setData(data)); }, []);
    return (
        <div className="space-y-4">
            <KpiGrid kpis={data?.kpis || []} accent="red" cols={4} />
            <div>
                <div className="kcc-label mb-2">Bad Actors — highest failures + lowest health</div>
                <DataTable
                    columns={[
                        { key: "tag", label: "Tag" },
                        { key: "name", label: "Asset" },
                        { key: "type", label: "Family" },
                        { key: "failures_ytd", label: "Failures YTD" },
                        { key: "health_index", label: "Health" },
                        { key: "dominant_failure_mode", label: "Dominant Mode" },
                    ]}
                    rows={data?.bad_actors || []}
                    testid="bad-actors"
                />
            </div>
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
                <div>
                    <div className="kcc-label mb-2">Downtime Pareto by Area</div>
                    <DataTable
                        columns={[{ key: "area", label: "Area" }, { key: "downtime_h", label: "Downtime (h)" }]}
                        rows={data?.downtime_pareto || []}
                        testid="downtime-pareto"
                    />
                </div>
                <div>
                    <div className="kcc-label mb-2">Failure Mode Distribution</div>
                    <DataTable
                        columns={[{ key: "mode", label: "Failure Mode" }, { key: "count", label: "Count" }]}
                        rows={data?.failure_modes || []}
                        testid="failure-modes"
                    />
                </div>
                <div>
                    <div className="kcc-label mb-2">Weibull β by Family</div>
                    <DataTable
                        columns={[
                            { key: "family", label: "Family" },
                            { key: "beta", label: "β" },
                            { key: "regime", label: "Regime", render: (r) => <StatusChip status={r.regime === "Wear-out" ? "warning" : r.regime === "Infant" ? "critical" : "normal"}>{r.regime}</StatusChip> },
                            { key: "assets", label: "Assets" },
                        ]}
                        rows={data?.weibull || []}
                        testid="weibull"
                    />
                </div>
            </div>
            <div className="kcc-label">MTBF by Family (ascending — worst first)</div>
            <DataTable
                columns={[
                    { key: "family", label: "Family" },
                    { key: "count", label: "Assets" },
                    { key: "mtbf", label: "MTBF (h)" },
                    { key: "mttr", label: "MTTR (h)" },
                    { key: "failures", label: "Failures YTD" },
                ]}
                rows={data?.mtbf_by_family || []}
                testid="mtbf-family"
            />
        </div>
    );
}

export function ContractorsPanel() {
    const [data, setData] = useState(null);
    useEffect(() => { api.get("/refinery-maintenance/contractors").then(({ data }) => setData(data)); }, []);
    return (
        <div className="space-y-4">
            <KpiGrid kpis={data?.kpis || []} accent="cobalt" cols={5} />
            <Panel title="Refinery Contractor Register" accent="cobalt">
                <DataTable
                    columns={[
                        { key: "name", label: "Firm" },
                        { key: "scope", label: "Scope" },
                        { key: "workforce_onsite", label: "Onsite" },
                        { key: "compliance_pct", label: "Compliance", render: (r) => `${r.compliance_pct}%` },
                        { key: "competency_pct", label: "Competency", render: (r) => `${r.competency_pct}%` },
                        { key: "safety_ltifr", label: "LTIFR" },
                        { key: "cost_ytd_usd", label: "Spend YTD", render: (r) => `$${r.cost_ytd_usd.toLocaleString()}` },
                        { key: "active_wos", label: "Active WOs" },
                        { key: "medical_valid", label: "Medical", render: (r) => <StatusChip status={r.medical_valid ? "normal" : "warning"}>{r.medical_valid ? "OK" : "Missing"}</StatusChip> },
                        { key: "safety_file_valid", label: "Safety File", render: (r) => <StatusChip status={r.safety_file_valid ? "normal" : "warning"}>{r.safety_file_valid ? "OK" : "Missing"}</StatusChip> },
                        { key: "insurance_valid", label: "Insurance", render: (r) => <StatusChip status={r.insurance_valid ? "normal" : "warning"}>{r.insurance_valid ? "OK" : "Missing"}</StatusChip> },
                        { key: "performance", label: "Performance", render: (r) => <StatusChip status={statusToChip(r.performance)}>{r.performance}</StatusChip> },
                    ]}
                    rows={data?.contractors || []}
                    testid="refinery-contractors"
                />
            </Panel>
        </div>
    );
}

export function KnowledgePanel() {
    const [docs, setDocs] = useState([]);
    const [q, setQ] = useState("");
    const [aiMsgs, setAiMsgs] = useState([]);
    const [aiInput, setAiInput] = useState("");
    const [aiSid, setAiSid] = useState(null);
    const [asking, setAsking] = useState(false);

    async function refresh(query) {
        const { data } = await api.get("/refinery-maintenance/knowledge" + (query ? `?q=${encodeURIComponent(query)}` : ""));
        setDocs(data.documents || []);
    }
    useEffect(() => { refresh(""); }, []);

    async function ask(text) {
        const message = (text ?? aiInput).trim();
        if (!message || asking) return;
        setAiInput("");
        setAiMsgs((m) => [...m, { role: "user", content: message }]);
        setAsking(true);
        try {
            const { data } = await api.post("/refinery-maintenance/knowledge/ask", { question: message, session_id: aiSid });
            setAiSid(data.session_id);
            setAiMsgs((m) => [...m, { role: "assistant", content: data.reply, sources: data.sources || [] }]);
        } catch (e) {
            setAiMsgs((m) => [...m, { role: "assistant", content: `⚠︎ ${e.response?.data?.detail || e.message}` }]);
        } finally { setAsking(false); }
    }

    return (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
            <Panel
                title="Engineering Knowledge Centre"
                subtitle={`${docs.length} refinery documents`}
                accent="purple"
                className="xl:col-span-2"
                actions={
                    <div className="flex items-center gap-2 bg-white/[0.03] border border-white/10 rounded-md px-2.5 py-1 w-72">
                        <Search className="w-3.5 h-3.5 kcc-text-silver" />
                        <input value={q} onChange={(e) => { setQ(e.target.value); refresh(e.target.value); }} placeholder="Search SOPs, manuals, lessons…" className="bg-transparent outline-none text-xs flex-1" data-testid="refinery-kb-search" />
                    </div>
                }
            >
                <DataTable
                    columns={[
                        { key: "doc_no", label: "Doc #" },
                        { key: "title", label: "Title" },
                        { key: "type", label: "Type" },
                        { key: "area", label: "Area" },
                        { key: "revision", label: "Rev" },
                        { key: "classification", label: "Class" },
                        { key: "views_30d", label: "Views (30d)" },
                    ]}
                    rows={docs}
                    testid="refinery-kb-table"
                />
            </Panel>
            <Panel title="Ask Engineering Knowledge AI" subtitle="Retrieval-Augmented Q&A over refinery docs" accent="purple" className="min-h-[560px] flex flex-col">
                <div className="flex-1 overflow-y-auto space-y-3 pr-1 max-h-[420px]">
                    {aiMsgs.length === 0 && <div className="text-sm kcc-text-silver bg-white/[0.03] border border-white/10 rounded-lg p-4">Ask questions such as <i>"Show every pump seal failure on electrowinning pumps"</i> or <i>"Recommend the best maintenance strategy for pump P-204"</i>.</div>}
                    {aiMsgs.map((m, i) => (
                        <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                            <div className={`max-w-[92%] rounded-lg px-3.5 py-2.5 text-sm ${m.role === "user" ? "bg-kcc-cobalt/15 border border-kcc-cobalt/25 text-white" : "bg-white/[0.03] border border-white/8 text-kcc-silver"}`}>
                                <div className="whitespace-pre-wrap">{m.content}</div>
                                {m.sources?.length > 0 && <div className="mt-2 pt-2 border-t border-white/5 text-[10.5px] kcc-text-dim flex flex-wrap gap-1.5"><Sparkles className="w-3 h-3 text-kcc-purple" />{m.sources.map((s) => <span key={s} className="px-1.5 py-0.5 rounded border border-white/10 font-mono-kcc">{s}</span>)}</div>}
                            </div>
                        </div>
                    ))}
                    {asking && <div className="text-sm kcc-text-silver flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Retrieving refinery knowledge…</div>}
                </div>
                <form onSubmit={(e) => { e.preventDefault(); ask(); }} className="mt-3 flex items-end gap-2">
                    <textarea value={aiInput} onChange={(e) => setAiInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); ask(); } }} rows={1} placeholder="Ask the Engineering Knowledge AI…" className="flex-1 bg-white/[0.03] border border-white/10 rounded-md px-3 py-2 text-sm outline-none focus:border-kcc-purple/50 resize-none max-h-32" data-testid="refinery-kb-ask" />
                    <button type="submit" disabled={asking || !aiInput.trim()} className="bg-kcc-copper hover:bg-kcc-copper-light disabled:opacity-40 text-white rounded-md px-3 py-2" data-testid="refinery-kb-send"><Send className="w-4 h-4" /></button>
                </form>
            </Panel>
        </div>
    );
}

export function ReportsPanel() {
    const [reports, setReports] = useState([]);
    const [generating, setGenerating] = useState(null);
    const [msg, setMsg] = useState("");
    async function refresh() {
        const { data } = await api.get("/refinery-maintenance/reports");
        setReports(data.reports || []);
    }
    useEffect(() => { refresh(); }, []);
    async function download(r) {
        setGenerating(r.id);
        setMsg("");
        try {
            const res = await fetch(`${API_BASE}/reports/generate/${r.id}`, {
                method: "POST", credentials: "include",
                headers: { Authorization: `Bearer ${localStorage.getItem("kcc_access_token") || ""}` },
            });
            if (!res.ok) throw new Error(await res.text());
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url; a.download = `KCC360_${r.name.replace(/\s+/g, "_")}.pdf`;
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
            setMsg(`Generated: ${r.name}. Signed, watermarked, audited.`);
        } catch (e) { setMsg(`Failed: ${e.message}`); }
        finally { setGenerating(null); }
    }
    return (
        <Panel title="Refinery Executive Reports" subtitle="Daily → Weekly → Monthly → Quarterly · signed & watermarked PDFs" accent="purple">
            {msg && <div className="mb-4 kcc-panel p-3 flex items-center gap-3 text-sm"><ShieldCheck className="w-4 h-4 text-kcc-green" /><div className="text-white">{msg}</div></div>}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {reports.map((r) => (
                    <div key={r.id} className="kcc-surface-elev p-4 flex flex-col gap-2" data-testid={`refinery-report-${r.id}`}>
                        <div className="flex items-start justify-between gap-2">
                            <div>
                                <div className="font-display text-base font-semibold text-white leading-tight">{r.name}</div>
                                <div className="text-[11px] kcc-text-silver mt-1">{r.cadence.toUpperCase()} · {r.area} · {r.recipients} recipients</div>
                            </div>
                        </div>
                        <p className="text-xs kcc-text-silver leading-relaxed">{r.description}</p>
                        <div className="text-[10.5px] kcc-text-dim">Last: {r.last_generated.slice(0, 10)} · {r.classification}</div>
                        <button onClick={() => download(r)} disabled={generating === r.id} className="mt-1 inline-flex items-center justify-center gap-2 bg-kcc-copper hover:bg-kcc-copper-light disabled:opacity-60 text-white rounded-md px-3 py-2 text-sm" data-testid={`generate-refinery-${r.id}`}>
                            {generating === r.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Generate signed PDF
                        </button>
                    </div>
                ))}
            </div>
        </Panel>
    );
}
